import { isDarkMode } from "../../../utils/constants";
import { parseRawDate } from "./utils";
import { getChartPalette } from "./chartPalette";
import { estimateBarSpacing } from "./chartOverlayCanvas";
import {
  colorWithAlpha,
  inferTradeSide,
  overlayAppliesToTicker,
  toTradeNumber,
} from "./chartOverlayUtils";

const pickTradeAnchorTimestampMs = (tradeProposal, messageThread) => {
  const candidates = [
    tradeProposal?.entry_bar_time,
    tradeProposal?.entryBarTime,
    tradeProposal?.entry_bar_datetime,
    tradeProposal?.entryBarDatetime,
    tradeProposal?.entry_bar_at,
    tradeProposal?.entryBarAt,
    tradeProposal?.current_price_updated_at,
    tradeProposal?.currentPriceUpdatedAt,
    tradeProposal?.created_at,
    tradeProposal?.createdAt,
    messageThread?.createdAt,
    messageThread?.created_at,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (candidate instanceof Date) {
      const ms = candidate.getTime();
      if (Number.isFinite(ms)) return ms;
      continue;
    }
    if (typeof candidate === "number") {
      if (Number.isFinite(candidate)) {
        return candidate < 1e12 ? candidate * 1000 : candidate;
      }
      continue;
    }
    const parsed = Date.parse(String(candidate));
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

const buildTradeAnchorIndex = ({
  tradeProposal,
  messageThread,
  tickerPrices,
  scale,
}) => {
  if (!Array.isArray(tickerPrices) || tickerPrices.length === 0) return null;

  const targetMs = pickTradeAnchorTimestampMs(tradeProposal, messageThread);
  if (!Number.isFinite(targetMs)) {
    return tickerPrices.length - 1;
  }

  let bestIndex = null;
  let bestDiff = Number.POSITIVE_INFINITY;

  tickerPrices.forEach((entry, idx) => {
    const rawTime =
      scale === "1d" || scale === "1w" || scale === "1mo"
        ? entry?.date ?? entry?.datetime
        : entry?.datetime ?? entry?.date;
    const parsed = parseRawDate(rawTime, scale);
    const candidateMs = parsed?.getTime?.();
    if (!Number.isFinite(candidateMs)) return;

    const diff = Math.abs(candidateMs - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = idx;
    }
  });

  return bestIndex != null ? bestIndex : tickerPrices.length - 1;
};

export const drawTradeOverlay = ({ chart, series, overlay, context, width, height }) => {
  if (!chart || !series || !context || !Number.isFinite(width) || !Number.isFinite(height)) {
    return;
  }

  if (!Array.isArray(overlay?.segments) || overlay.segments.length === 0) {
    return;
  }

  const timeScale = chart.timeScale?.();
  const visibleRange = timeScale?.getVisibleLogicalRange?.() || null;
  const rightCoordinateRaw = visibleRange
    ? timeScale?.logicalToCoordinate?.(visibleRange.to)
    : null;
  const rightCoordinate = Number.isFinite(rightCoordinateRaw)
    ? Math.max(0, Math.min(rightCoordinateRaw, width))
    : width;
  const barSpacing = estimateBarSpacing(chart, width);
  const markerOffset = barSpacing * 0.2;
  const guideOffset = barSpacing * 0.28;

  overlay.segments.forEach((segment) => {
    if (
      !segment ||
      !Number.isFinite(segment.value) ||
      (!Number.isFinite(segment.startIndex) &&
        !Number.isFinite(segment.startTime))
    ) {
      return;
    }

    const startCoordinateRaw = Number.isFinite(segment.startIndex)
      ? timeScale?.logicalToCoordinate?.(segment.startIndex)
      : timeScale?.timeToCoordinate?.(segment.startTime);
    const yCoordinate = series.priceToCoordinate?.(segment.value);

    if (
      !Number.isFinite(startCoordinateRaw) ||
      !Number.isFinite(yCoordinate) ||
      yCoordinate < -2 ||
      yCoordinate > height + 2
    ) {
      return;
    }

    const segmentStart = Math.min(startCoordinateRaw, rightCoordinate);
    const segmentEnd =
      segment.kind === "entry"
        ? Math.min(
            segmentStart +
              markerOffset +
              Math.max(rightCoordinate - segmentStart - markerOffset, 0) * 0.25,
            rightCoordinate
          )
        : rightCoordinate;
    const drawStart =
      segment.kind === "entry"
        ? Math.min(segmentStart + guideOffset, segmentEnd)
        : segmentStart;

    if (!Number.isFinite(segmentEnd) || segmentEnd <= drawStart) {
      return;
    }

    context.save();
    context.strokeStyle = segment.color;
    context.lineWidth = segment.lineWidth || 2;
    context.lineCap = segment.dashed ? "butt" : "round";
    if (segment.dashed) {
      context.setLineDash([6, 5]);
    } else {
      context.setLineDash([]);
    }
    const alignedY = Math.round(yCoordinate) + 0.5;
    context.beginPath();
    context.moveTo(drawStart, alignedY);
    context.lineTo(segmentEnd, alignedY);
    context.stroke();
    context.restore();
  });
};

const colorForOverlayRole = (role) => {
  const palette = getChartPalette();
  switch (String(role || "").toLowerCase()) {
    case "support":
      return palette.target;
    case "resistance":
      return palette.stop;
    case "entry":
      return palette.entry;
    case "stop_loss":
      return palette.stop;
    case "price_target":
      return palette.target;
    case "vwap":
      return palette.extendedHours;
    default:
      return isDarkMode ? "rgba(203,213,225,0.84)" : "rgba(71,85,105,0.84)";
  }
};

const compactOverlayLabel = (role) => {
  switch (String(role || "").toLowerCase()) {
    case "support":
      return "Support";
    case "resistance":
      return "Resistance";
    case "entry":
      return "Entry";
    case "stop_loss":
      return "Invalidation";
    case "price_target":
      return "Target";
    case "vwap":
      return "VWAP";
    default:
      return "Level";
  }
};

const clusterWorkspacePriceLines = (priceLines, zones) =>
  priceLines.map((priceLine, index, rows) => {
    const nearPriceLine = rows.some((candidate, candidateIndex) => {
      if (candidateIndex === index) return false;
      const denominator = Math.max(
        Math.abs(priceLine.value),
        Math.abs(candidate.value),
        1,
      );
      return Math.abs(priceLine.value - candidate.value) / denominator <= 0.004;
    });
    const nearZone = zones.some((zone) =>
      [zone.top, zone.bottom].some((boundary) => {
        const denominator = Math.max(
          Math.abs(priceLine.value),
          Math.abs(boundary),
          1,
        );
        return Math.abs(priceLine.value - boundary) / denominator <= 0.004;
      }),
    );
    const clustered = nearPriceLine || nearZone;
    return clustered
      ? { ...priceLine, label: "", axisLabelVisible: false }
      : priceLine;
  });

export const buildAgenticOverlayConfig = ({
  agenticChartState,
  compactLabels = false,
  selectedTickerId,
}) => {
  if (!agenticChartState || typeof agenticChartState !== "object") {
    return { priceLines: [], zones: [] };
  }

  const overlays = Array.isArray(agenticChartState.overlays)
    ? agenticChartState.overlays
    : [];
  const priceLines = [];
  const zones = [];

  overlays.forEach((overlay) => {
    if (!overlay || typeof overlay !== "object") return;
    if (!overlayAppliesToTicker(overlay, selectedTickerId)) return;

    const kind = String(overlay.kind || "").toLowerCase();
    const semanticRole = overlay.semantic_role || overlay.semanticRole || "note";
    const color = colorForOverlayRole(semanticRole);
    const label = compactLabels
      ? compactOverlayLabel(semanticRole)
      : overlay.label ||
        String(semanticRole || "Level").replace(/_/g, " ");

    if (kind === "horizontal_line") {
      const value = toTradeNumber(overlay.price ?? overlay.value);
      if (value == null) return;
      priceLines.push({
        value,
        color,
        label,
        lineWidth: 2,
        dashed: semanticRole !== "entry",
      });
      return;
    }

    if (kind === "price_zone") {
      const top = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
      const bottom = toTradeNumber(overlay.bottom ?? overlay.lower ?? overlay.low);
      if (top == null || bottom == null) return;
      zones.push({
        top: Math.max(top, bottom),
        bottom: Math.min(top, bottom),
        color,
        label,
        labelInset: compactLabels ? 78 : 8,
      });
    }
  });

  return {
    priceLines: compactLabels
      ? clusterWorkspacePriceLines(priceLines, zones)
      : priceLines,
    zones,
  };
};

export const drawAgenticOverlay = ({ series, overlay, context, width, height }) => {
  if (!series || !context || !Number.isFinite(width) || !Number.isFinite(height)) {
    return;
  }

  const zones = Array.isArray(overlay?.zones) ? overlay.zones : [];
  zones.forEach((zone) => {
    const yTop = series.priceToCoordinate?.(zone.top);
    const yBottom = series.priceToCoordinate?.(zone.bottom);
    if (!Number.isFinite(yTop) || !Number.isFinite(yBottom)) return;

    const top = Math.max(0, Math.min(yTop, yBottom));
    const bottom = Math.min(height, Math.max(yTop, yBottom));
    if (bottom <= top) return;

    context.save();
    context.fillStyle = colorWithAlpha(zone.color || "#64748b", 0.13);
    context.strokeStyle = colorWithAlpha(zone.color || "#64748b", 0.5);
    context.lineWidth = 1;
    context.setLineDash([6, 5]);
    context.fillRect(0, top, width, bottom - top);
    context.strokeRect(0.5, top + 0.5, Math.max(width - 1, 0), Math.max(bottom - top - 1, 0));
    if (zone.label) {
      context.setLineDash([]);
      context.fillStyle = zone.color || getChartPalette().text;
      context.font = "600 11px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
      context.textAlign = "right";
      context.textBaseline = "top";
      context.fillText(zone.label, width - (zone.labelInset || 8), top + 5);
    }
    context.restore();
  });
};

export const buildTradeOverlayConfig = ({
  messageThread,
  selectedTickerId,
  tickerPrices,
  scale,
  priceTimes,
}) => {
  const refinedQueryObj =
    messageThread?.refinedQueryObj &&
    typeof messageThread.refinedQueryObj === "object"
      ? messageThread.refinedQueryObj
      : null;
  const tradeProposal =
    refinedQueryObj?.trade_proposed || refinedQueryObj?.tradeProposed || null;

  if (!tradeProposal) {
    return { segments: [], markers: [], priceLines: [] };
  }

  const tradeEntry = toTradeNumber(
    tradeProposal.entry_price ?? tradeProposal.entryPrice
  );
  const tradeStop = toTradeNumber(
    tradeProposal.stop_loss ?? tradeProposal.stopLoss
  );
  const tradeTarget = toTradeNumber(
    tradeProposal.price_target ??
      tradeProposal.priceTarget ??
      tradeProposal.profit_target ??
      tradeProposal.profitTarget
  );
  const tradeSide = inferTradeSide(tradeEntry, tradeStop, tradeTarget);
  const tradeTickerId =
    tradeProposal?.ticker_id ?? tradeProposal?.tickerId ?? null;
  const tradeTickerIdStr =
    tradeTickerId != null ? String(tradeTickerId) : null;
  const selectedTickerIdStr =
    selectedTickerId != null ? String(selectedTickerId) : null;

  if (
    tradeEntry == null &&
    tradeStop == null &&
    tradeTarget == null
  ) {
    return { segments: [], markers: [], priceLines: [] };
  }

  if (
    tradeTickerIdStr &&
    selectedTickerIdStr &&
    tradeTickerIdStr !== selectedTickerIdStr
  ) {
    return { segments: [], markers: [], priceLines: [] };
  }

  const palette = getChartPalette();
  const entryColor = palette.entry;
  const rawAnchorIndex = buildTradeAnchorIndex({
    tradeProposal,
    messageThread,
    tickerPrices,
    scale,
  });
  const anchorIndex =
    rawAnchorIndex == null || !Array.isArray(priceTimes) || priceTimes.length === 0
      ? null
      : Math.max(0, Math.min(rawAnchorIndex, priceTimes.length - 1));
  const anchorTime =
    anchorIndex != null ? priceTimes[anchorIndex] : null;

  const segments = [];
  const priceLines = [];
  if (anchorIndex != null && tradeTarget != null) {
    segments.push({
      kind: "target",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeTarget,
      color: palette.target,
      lineWidth: 2,
      dashed: true,
    });
    priceLines.push({
      label: "Target",
      value: tradeTarget,
      color: palette.target,
      lineWidth: 2,
      dashed: true,
    });
  }
  if (anchorIndex != null && tradeStop != null) {
    segments.push({
      kind: "stop",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeStop,
      color: palette.stop,
      lineWidth: 2,
      dashed: true,
    });
    priceLines.push({
      label: "Stop",
      value: tradeStop,
      color: palette.stop,
      lineWidth: 2,
      dashed: true,
    });
  }
  if (anchorIndex != null && tradeEntry != null) {
    segments.push({
      kind: "entry",
      startIndex: anchorIndex,
      startTime: anchorTime,
      value: tradeEntry,
      color: entryColor,
      lineWidth: 2,
      dashed: false,
    });
    priceLines.push({
      label: "Entry",
      value: tradeEntry,
      color: entryColor,
      lineWidth: 2,
      dashed: false,
    });
  }

  const markers = [];
  if (anchorTime != null && tradeEntry != null) {
    markers.push({
      time: anchorTime,
      position: tradeSide === "short" ? "aboveBar" : "belowBar",
      color: entryColor,
      shape:
        tradeSide === "short"
          ? "arrowDown"
          : tradeSide === "long"
            ? "arrowUp"
            : "circle",
      text: "",
    });
  }

  return { segments, markers, priceLines };
};
